import { useState } from "react"
import { Badge } from "@workspace/web-ui/components/badge"
import { cn } from "@workspace/web-ui/lib/utils"
import { format, isThisHour } from "date-fns"
import type { CalendarEvent, AppointmentEvent, RefillEvent } from "./types"
import { Clock, Globe, Mail, User } from "lucide-react"
import { Button } from "@workspace/web-ui/components/button"
import { MedicalCalendarRefillComponent } from "~/features/dashboard/components/refills/refill-content"
import { AppointmentDetailsFromData } from "./appointment-details-content"
import { useParams } from "@tanstack/react-router"

interface ExpandableAppointmentCardProps {
  appointment: CalendarEvent
  isExpanded?: boolean
  onToggle?: (isExpanding: boolean) => void
}

const formatTimeRange = (start: Date, end: Date) => {
  return `${format(start, "h:mm a")} - ${format(end, "h:mm a")}`
}

const formatFullDateTime = (date: Date) => {
  return format(date, "h:mm a, EEEE, MMMM d, yyyy")
}

const getEventColor = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return event.status === 'cancelled'
      ? "border border-white bg-[#ECECED] text-[#2D2F35]"
      : event.status === 'completed'
        ? "bg-[#E1FAEA] border border-green-200 text-[#2D2F35]"
        : isThisHour(event.start) ? "bg-[#019939] text-white"
          : "bg-white border border-[#019939] text-[#019939]"
  } else {
    return event.status === 'pending'
      ? "text-white bg-[#4272DD] border border-white"
      : event.status === 'accepted'
        ? "bg-white border border-[#4272DD] text-[#2D2F35]"
        : "bg-[#ECECED] text-[#2D2F35]"
  }
}

const getStatusBadgeStyles = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return event.status === 'cancelled'
      ? "text-[#202020CC] bg-gray-300 border border-gray-300 hover:bg-gray-300"
      : event.status === 'completed'
        ? "text-[#14AD4D] hover:bg-transparent bg-transparent border border-[#14AD4D]"
        : isThisHour(event.start) ? "border-[#FFFFFF59]"
          : "bg-[#00000014] hover:bg-[#019939] text-[#2D2F35]"
  } else {
    return event.status === 'pending'
      ? "text-[#4272DD] bg-white border border-white"
      : event.status === 'accepted'
        ? "text-blue-600 hover:bg-transparent bg-transparent border border-blue-600"
        : "bg-white text-[#202020CC]"
  }
}

const getEventStatus = (event: CalendarEvent) => {
  if (event.type === 'appointment') {
    return event.status === "cancelled" ? "cancelled" : event.status === "completed" ? "completed" : "upcoming"
  } else {
    if (event.status) return event.status
    return event.status === 'pending' ? "Pending" : event.status === 'accepted' ? "Accepted" : "Rejected"
  }
}

const EventContent = ({ event }: { event: CalendarEvent }) => {
  if (event.type === 'appointment') {
    const appointmentEvent = event as AppointmentEvent
    return (
      <>
        <div className="flex items-center space-x-3 mb-1 w-full max-w-[800px]">
          <p className="font-medium">{appointmentEvent.clientName} Weight loss consultation</p>
          <Badge
            className={cn(
              "rounded-full px-2 text-xs font-medium capitalize",
              getStatusBadgeStyles(appointmentEvent)
            )}
          >
            {getEventStatus(appointmentEvent)}
          </Badge>
        </div>
        <p className="text-sm">{formatTimeRange(appointmentEvent.start, appointmentEvent.end)}</p>
      </>
    )
  } else {
    const refillEvent = event as RefillEvent
    return (
      <>
        <div className="flex items-center space-x-3 mb-1">
          <p className="font-medium">Refill: {refillEvent.medication}</p>
          <Badge
            className={cn(
              "rounded-full px-2 text-xs font-medium capitalize",
              getStatusBadgeStyles(refillEvent)
            )}
          >
            {getEventStatus(refillEvent)}
          </Badge>
        </div>
        <p className="text-sm">{formatTimeRange(refillEvent.start, refillEvent.end)}</p>
        <p className="text-sm mt-1">Patient: {refillEvent.clientName}</p>
        <p className="text-sm">Quantity: {refillEvent.quantity}</p>
      </>
    )
  }
}

export const EventDetails = ({ event }: { event: CalendarEvent }) => {
  if (event.type === 'appointment') {
    const appointmentEvent = event as AppointmentEvent
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <User className="h-4 w-4 text-gray-500" />
          <p className="text-sm">{appointmentEvent.clientName}</p>
        </div>
        {appointmentEvent.email && (
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-gray-500" />
            <p className="text-sm">{appointmentEvent.email}</p>
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <p className="text-sm">{formatFullDateTime(appointmentEvent.start)}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Globe className="h-4 w-4 text-gray-500" />
          <p className="text-sm">West Africa Time</p>
        </div>
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <p className="text-sm">30 minutes before</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="h-4 w-4 rounded-full bg-gray-300" />
          <p className="text-sm capitalize">{getEventStatus(appointmentEvent)}</p>
        </div>
        <div className="text-xs text-gray-500 mt-4">
          Created {format(new Date(appointmentEvent.start || Date.now()), "d MMMM yyyy")}
        </div>
        {/* {appointmentEvent.meetingUrl && ( */}
        <div className="mt-4 w-full flex justify-end">
          <Button className="rounded-[8px] bg-[#4272DD] hover:bg-[#4272DD]" size="sm">
            Join meeting
          </Button>
        </div>
        {/* )} */}
      </div>
    )
  } else {
    const refillEvent = event as RefillEvent
    return (
      // <div className="pt-4 border-t mt-4 space-y-3">
      //   <div className="flex items-center space-x-2">
      //     <User className="h-4 w-4 text-gray-500" />
      //     <p className="text-sm">{refillEvent.clientName}</p>
      //   </div>
      //   <div className="flex items-center space-x-2">
      //     <Clock className="h-4 w-4 text-gray-500" />
      //     <p className="text-sm">{formatFullDateTime(refillEvent.start)}</p>
      //   </div>
      //   <div className="flex items-center space-x-2">
      //     <div className="h-4 w-4 rounded-full bg-gray-300" />
      //     <p className="text-sm capitalize">{getEventStatus(refillEvent)}</p>
      //   </div>
      //   <div className="text-xs text-gray-500 mt-4">
      //     Created {format(new Date(refillEvent.start || Date.now()), "d MMMM yyyy")}
      //   </div>
      // </div>
      <MedicalCalendarRefillComponent
        refillId={refillEvent.id}
      />
    )
  }
}

export function ExpandableAppointmentCard({
  appointment,
  isExpanded: controlledIsExpanded,
  onToggle
}: ExpandableAppointmentCardProps) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false)
  const { program = "" } = useParams({ strict: false });

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!isExpanded)
    } else {
      setInternalIsExpanded(!isExpanded)
    }
  }

  const borderColor = appointment.type === 'appointment'
    ? "border-l-green-500"
    : "border-l-blue-500"

  return (
    <div className="mb-4 transition-all duration-200 ease-in-out">
      <div
        onClick={handleToggle}
        className={cn(
          "p-4 rounded-t-md flex flex-col cursor-pointer relative",
          isExpanded ? "rounded-b-none" : "rounded-md",
          getEventColor(appointment)
        )}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <EventContent event={appointment} />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className={cn(
          "p-4 rounded-b-md border-l-4 border border-t-0 bg-white",
          borderColor
        )}>
          {program === "urgent-care" ? (
            <AppointmentDetailsFromData appointment={appointment as AppointmentEvent} />
          ) : (
            <EventDetails event={appointment} />
          )}
        </div>
      )}
    </div>
  )
}